import React from 'react';
import { Box, ButtonBase, Card, Grid, Stack, Typography } from '@mui/material';
import { LocationFilter } from '../types';
import { LocationGroup, aggregateTeam, formatDecimal, formatInt, pctDelta, signed } from '../utils/metrics';
import { sumSuccessUpdates } from '../utils/successUpdates';
import { DeltaText } from './DeltaText';

export interface LocationCardsProps {
  groups: LocationGroup[];
  selected: LocationFilter;
  onSelect: (value: LocationFilter) => void;
  /** Adds the overdue-updates rows; off until the success-update data is loaded. */
  showUpdates?: boolean;
}

const filterValue = (group: LocationGroup): LocationFilter => (group.location === null ? 'NONE' : group.location);

const plain = (value: number | null): string => (value === null ? '-' : Number.isInteger(value) ? formatInt(value) : formatDecimal(value));

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ borderTop: '1px solid', borderColor: 'grey.100', pt: 0.75 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
      {value}
    </Typography>
  </Stack>
);

const na = (
  <Typography component="span" variant="caption" sx={{ color: 'text.disabled', fontWeight: 500 }}>
    n/a
  </Typography>
);

/** One card per home location with that location's aggregates; clicking a card filters the table. */
export const LocationCards: React.FC<LocationCardsProps> = ({ groups, selected, onSelect, showUpdates = false }) => (
  <Grid container spacing={2}>
    {groups.map((group) => {
      const agg = aggregateTeam(group.records);
      const updates = showUpdates ? sumSuccessUpdates(group.records) : null;
      const value = filterValue(group);
      const isSelected = selected === value;
      return (
        <Grid item xs={12} sm={6} lg={3} key={value}>
          <ButtonBase
            aria-label={`${group.label} location`}
            aria-pressed={isSelected}
            onClick={() => onSelect(isSelected ? 'ALL' : value)}
            sx={{ display: 'block', textAlign: 'left', width: '100%', height: '100%', borderRadius: 3 }}
          >
            <Card
              elevation={0}
              sx={{
                height: '100%',
                p: 2,
                border: '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                boxShadow: isSelected ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : undefined,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography variant="subtitle2" noWrap>
                    {group.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {agg.activeCount} of {agg.staffCount} active
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                    {formatInt(agg.sessionsLatest)}
                  </Typography>
                  {agg.sessionsChangePct === null ? (
                    <Typography variant="caption" color="text.secondary">
                      prior {formatInt(agg.sessionsPrior)}
                    </Typography>
                  ) : (
                    <DeltaText delta={pctDelta(agg.sessionsChangePct)} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
                  sessions · {plain(agg.meanPerActiveCoachLatest)} per active coach / month
                </Typography>
                <Row label="Net clients" value={agg.coachesWithClients > 0 ? signed(agg.netLatest) : na} />
                <Row label="Conversion" value={agg.rateLatest !== null ? `${formatDecimal(agg.rateLatest)}%` : na} />
                {showUpdates ? (
                  <Row
                    label="Overdue updates"
                    value={updates && updates.active > 0 ? `${formatInt(updates.overdue)} of ${formatInt(updates.active)}` : na}
                  />
                ) : null}
                {updates && updates.awaiting_first > 0 ? <Row label="Awaiting a first update" value={formatInt(updates.awaiting_first)} /> : null}
              </Stack>
            </Card>
          </ButtonBase>
        </Grid>
      );
    })}
  </Grid>
);
